/**
 * 라이브러리 가족 — user_content.versions 를 읽고 옛 문서를 1버전으로 해석한다.
 */

import { parseLibraryTime } from './libraryTimestamps';

const VERSION_KEYS = ['text', 'image'];

export const parseOutline = (value) => {
  if (!value) return null;
  if (typeof value === 'object') {
    if (Array.isArray(value)) return value.length ? value : null;
    return Object.keys(value).length ? value : null;
  }
  if (typeof value === 'string') {
    try { return parseOutline(JSON.parse(value)); } catch (_error) { return null; }
  }
  return null;
};

const cleanUrl = (value) => (typeof value === 'string' && value.trim() ? value.trim() : '');

const cleanUrls = (values) => {
  if (!Array.isArray(values)) return [];
  return values.map(cleanUrl).filter(Boolean);
};

/** 개요에 본문(슬라이드·문항·섹션)이 있는지 — 존재 판정용. 쪽수가 아니다. */
const outlineBodyCount = (outline) => {
  if (!outline || typeof outline !== 'object') return 0;
  if (Array.isArray(outline.slides) && outline.slides.length) return outline.slides.length;
  if (Array.isArray(outline.questions) && outline.questions.length) return outline.questions.length;
  if (Array.isArray(outline.sections) && outline.sections.length) return outline.sections.length;
  return 0;
};

/**
 * 개요로 추정할 수 있는 쪽수. PPT 는 슬라이드 수가 곧 쪽수지만, 학습지의 문항·섹션 수는
 * 쪽수가 아니다(16문항 학습지가 「16쪽」으로 표시되던 문제). 모르면 0 → 라벨을 숨긴다.
 */
const outlinePageCount = (outline) => {
  if (!outline || typeof outline !== 'object') return 0;
  if (Array.isArray(outline.slides) && outline.slides.length) return outline.slides.length;
  return 0;
};

export const KNOWN_FORMATS = ['hwpx', 'pdf', 'docx', 'pptx', 'png'];

/** 파일명 또는 URL 의 확장자. URL 의 ?query/#hash 는 확장자가 아니다. */
export const filenameExt = (filename) => {
  const name = String(filename || '').toLowerCase().split(/[?#]/)[0];
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot >= 0 ? base.slice(dot + 1) : '';
};

const formatFromFilename = (filename, contentType) => {
  const ext = filenameExt(filename);
  if (KNOWN_FORMATS.includes(ext)) return ext;
  if (contentType === 'ppt') return 'pptx';
  if (contentType === 'image') return 'png';
  return 'pdf';
};

/** 실제 파일(URL)의 확장자를 먼저 믿고, 없을 때만 대표 파일명·콘텐츠 종류로 추정한다. */
export const formatFromUrlOrFilename = (url, filename, contentType) => {
  const urlExt = filenameExt(url);
  if (KNOWN_FORMATS.includes(urlExt)) return urlExt;
  return formatFromFilename(filename, contentType);
};

/**
 * 한 버전 슬롯의 filename 은 하나인데 file_urls 는 형식별로 여럿이다.
 * 형식이 다른 파일에 그 이름을 그대로 붙이면 HWPX 가 `.pdf` 로 저장돼 한글에서 열리지 않는다.
 * 확장자만 해당 형식으로 맞춘다.
 */
export const filenameForFormat = (filename, format) => {
  const name = String(filename || '').trim();
  const fmt = String(format || '').toLowerCase();
  if (!name || !fmt) return name;
  const ext = filenameExt(name);
  if (ext === fmt) return name;
  const stem = ext ? name.slice(0, name.length - ext.length - 1) : name;
  return `${stem}.${fmt}`;
};

export const normalizeFamilyVersion = (slot, contentType = 'ppt') => {
  if (!slot || typeof slot !== 'object') return null;
  const fileUrls = {};
  const rawFiles = slot.file_urls && typeof slot.file_urls === 'object' ? slot.file_urls : {};
  Object.entries(rawFiles).forEach(([key, url]) => {
    const cleaned = cleanUrl(url);
    if (cleaned) fileUrls[String(key).toLowerCase()] = cleaned;
  });
  const fileUrl = cleanUrl(slot.file_url);
  if (fileUrl) {
    // 파일 URL 의 확장자를 먼저 본다. 대표 filename 이 다른 형식(.pdf)이거나 비어 있어도
    // 학습지 파일이 pptx 칸에 들어가지 않게 한다.
    const fmt = formatFromUrlOrFilename(fileUrl, slot.filename, contentType);
    if (!fileUrls[fmt]) fileUrls[fmt] = fileUrl;
  }
  const pageUrls = cleanUrls(slot.page_urls || slot.preview_urls);
  const outline = parseOutline(slot.outline);
  const pageCount = Number(slot.page_count || slot.pageCount || pageUrls.length || outlinePageCount(outline) || 0);
  if (!fileUrl && !Object.keys(fileUrls).length && !pageUrls.length && !outline) return null;
  return {
    file_urls: fileUrls,
    file_url: fileUrl || fileUrls.pptx || fileUrls.pdf || Object.values(fileUrls)[0] || '',
    page_urls: pageUrls,
    page_count: pageCount,
    updated_at: slot.updated_at || slot.updatedAt || '',
    outline: outline || null,
    outline_ref: slot.outline_ref || slot.outlineVersionId || null,
    filename: slot.filename || '',
  };
};

const attachOutlineText = (versions, item) => {
  const next = { ...(versions || {}) };
  const outline = parseOutline(next.text?.outline || item?.outline);
  if (outline && outlineBodyCount(outline) > 0 && !next.text) {
    next.text = normalizeFamilyVersion({
      outline,
      page_count: outlinePageCount(outline),
      updated_at: item?.updated_at || item?.updatedAt || item?.created_at || item?.createdAt || '',
    });
  }
  return next;
};

export const hydrateLegacyFamily = (item) => {
  if (!item || (item.content_type !== 'ppt' && item.content_type !== 'worksheet')) return {};
  const artifactMode = item.artifact?.sourceMode || item.source_mode || item.sourceMode;
  const previews = cleanUrls(item.preview_urls);
  let mode = null;
  if (artifactMode === 'text' || artifactMode === 'image') mode = artifactMode;
  else if (previews.length > 1) mode = 'image';
  else if (['hwpx', 'docx'].includes(filenameExt(item.filename))) mode = 'text';
  else if (parseOutline(item.outline)) mode = 'text';
  else if (item.download_url) mode = previews.length > 1 ? 'image' : 'text';
  if (!mode) return attachOutlineText({}, item);
  const fmt = formatFromUrlOrFilename(item.download_url, item.filename, item.content_type);
  return attachOutlineText({
    [mode]: normalizeFamilyVersion({
      file_urls: item.download_url ? { [fmt]: item.download_url } : {},
      file_url: item.download_url,
      page_urls: mode === 'image' ? previews : [],
      page_count: item.artifact?.pageCount || (mode === 'image' ? previews.length : outlinePageCount(item.outline)),
      outline: item.outline,
      filename: item.filename,
    }),
  }, item);
};

export const familyVersions = (item) => {
  const raw = item?.versions;
  if (raw && typeof raw === 'object') {
    const normalized = {};
    VERSION_KEYS.forEach((key) => {
      const slot = normalizeFamilyVersion(raw[key], item?.content_type);
      if (slot) normalized[key] = slot;
    });
    const withText = attachOutlineText(normalized, item);
    if (Object.keys(withText).length) return withText;
  }
  return hydrateLegacyFamily(item);
};

export const familyHasVersion = (item, mode) => {
  if (familyVersions(item)[mode]) return true;
  if (item?.versions_summary?.[mode]) return true;
  return false;
};

export const familyBadgeLabel = (item) => {
  if (!item || (item.content_type !== 'ppt' && item.content_type !== 'worksheet')) return null;
  const versions = familyVersions(item);
  const hasText = Boolean(versions.text);
  const hasImage = Boolean(versions.image);
  if (hasText && hasImage) return '텍스트·이미지';
  if (hasText) return '텍스트';
  if (hasImage) return '이미지';
  const summary = item.versions_summary;
  if (summary?.text && summary?.image) return '텍스트·이미지';
  if (summary?.text) return '텍스트';
  if (summary?.image) return '이미지';
  return null;
};

export const defaultFamilyViewMode = (item) => {
  const versions = familyVersions(item);
  const latest = item?.latest_version;
  if ((latest === 'text' || latest === 'image') && versions[latest]) return latest;
  const recent = mostRecentFamilyVersion(item);
  if (recent && versions[recent]) return recent;
  const textHasFile = Boolean(versions.text?.file_url);
  const imageHasFile = Boolean(versions.image?.file_url);
  if (imageHasFile && !textHasFile) return 'image';
  if (textHasFile && !imageHasFile) return 'text';
  if (versions.image && !versions.text) return 'image';
  if (versions.text) return 'text';
  if (versions.image) return 'image';
  return null;
};

export const familyOutline = (item, mode = 'text') => {
  const versions = familyVersions(item);
  if (mode === 'text') {
    return versions.text?.outline || parseOutline(item?.outline) || null;
  }
  return null;
};

export const pickLibraryRemakeOutline = (item, conversation) => {
  const family = familyOutline(item, 'text');
  const itemOutline = parseOutline(item?.outline);
  if (item?.content_type === 'ppt') {
    return pickNewerOutline(conversation?.pptOutline, family, itemOutline);
  }
  if (item?.content_type === 'worksheet') {
    return pickNewerOutline(
      conversation?.worksheetCanvas,
      conversation?.worksheetOutline,
      family,
      itemOutline,
    );
  }
  return family || itemOutline || null;
};

const pickNewerOutline = (preferred, ...rest) => {
  // 「더 긴 개요」비교는 본문 항목 수(슬라이드·문항·섹션)로 한다. 쪽수 추정과는 다르다.
  let best = preferred && typeof preferred === 'object' ? preferred : null;
  let bestCount = outlineBodyCount(best);
  rest.forEach((candidate) => {
    if (!candidate || typeof candidate !== 'object') return;
    const count = outlineBodyCount(candidate);
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  });
  return best;
};

export const familyPageUrls = (item) => {
  const versions = familyVersions(item);
  if (versions.image?.page_urls?.length) return versions.image.page_urls;
  const previews = cleanUrls(item?.preview_urls);
  return previews.length > 1 ? previews : [];
};

export const familyTextPageUrls = (item) => {
  const versions = familyVersions(item);
  return versions.text?.page_urls || [];
};

export const mostRecentFamilyVersion = (item) => {
  const versions = familyVersions(item);
  const summary = item?.versions_summary || {};
  let best = null;
  let bestTs = -1;
  VERSION_KEYS.forEach((mode) => {
    if (!versions[mode] && !summary[mode]) return;
    const ts = parseLibraryTime(
      versions[mode]?.updated_at || summary[mode]?.updated_at,
    );
    if (best == null || ts > bestTs || (ts === bestTs && mode === 'image')) {
      best = mode;
      bestTs = ts;
    }
  });
  return best;
};

export const familyCardThumbnail = (item) => {
  const versions = familyVersions(item);
  const recent = mostRecentFamilyVersion(item);
  const candidates = [recent, 'image', 'text'];
  for (let i = 0; i < candidates.length; i += 1) {
    const mode = candidates[i];
    const urls = versions[mode]?.page_urls;
    if (urls && urls.length) return urls[0];
  }
  return item?.thumbnail_url || '';
};

export const familyPageLabel = (item) => {
  if (!item || (item.content_type !== 'ppt' && item.content_type !== 'worksheet')) return '';
  const versions = familyVersions(item);
  const summary = item.versions_summary || {};
  const text = Number(versions.text?.page_count || summary.text?.page_count || 0) || 0;
  const image = Number(versions.image?.page_count || summary.image?.page_count || 0) || 0;
  if (text && image && text !== image) return `텍스트 ${text}쪽 · 이미지 ${image}쪽`;
  if (text && image) return `${text}쪽`;
  if (text) return `${text}쪽`;
  if (image) return `${image}쪽`;
  return '';
};

export const recordsFromFamilyVersions = (item) => {
  const versions = familyVersions(item);
  if (!versions || !Object.keys(versions).length) return [];
  const records = [];
  VERSION_KEYS.forEach((mode) => {
    const slot = versions[mode];
    if (!slot) return;
    const files = slot.file_urls || {};
    const formats = Object.keys(files);
    if (formats.length === 0 && slot.file_url) {
      const format = formatFromUrlOrFilename(slot.file_url, slot.filename, item?.content_type);
      records.push({
        sourceMode: mode,
        format,
        downloadUrl: slot.file_url,
        filename: filenameForFormat(slot.filename, format),
        pageCount: slot.page_count || 0,
        previewUrls: slot.page_urls || [],
      });
      return;
    }
    formats.forEach((format) => {
      records.push({
        sourceMode: mode,
        format,
        downloadUrl: files[format],
        filename: filenameForFormat(slot.filename, format),
        pageCount: slot.page_count || 0,
        previewUrls: slot.page_urls || [],
      });
    });
  });
  return records;
};
